import { useCallback, useEffect, useState, useMemo, type FC, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  parseToInquiryStage,
  useInquiryList,
  useInquirySummary,
  type Inquiry,
  type InquiryStage
} from "../../hooks/use-inquiries";
import { formatDateString } from "../forms/form-helpers";
import {
  type SortConfig,
  type SortDirection,
  SortableTable,
  type SortableTableHeader,
  type UsePaginatedSorterHook
} from "../table";
import { usePaginator } from "../pagination";
import { useDynamicPageSize } from "../table/use-dynamic-page-size";
import { useInquiryStatus } from "../../hooks/use-web-socket/use-inquiry-status";
import { useAccessControl } from "../../app/use-access-control";
import { useCenters } from "../../hooks/use-center";
import { Confirm, ConfirmHandle } from "../confirm/confirm";
import { useAccessToken } from "../../hooks/use-access-token";
import { useMyProfile } from "../../hooks/use-my-profile";
import { getStaffMemberPermissions } from "../../hooks/use-staff";
import { useQueryClient } from "@tanstack/react-query";

export interface InquiryListProps {
  preselectedInquiryId: string | undefined;
  search?: string;
  onInquirySelect?: (inquiry: Inquiry) => void;
  onStatusChange?: () => void;
  tableRef?: React.RefObject<HTMLTableElement>;
}

export const InquiryList: FC<InquiryListProps> = ({
  preselectedInquiryId,
  search,
  onInquirySelect,
  onStatusChange,
  tableRef
}) => {
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry>();
  const [hasInitialData, setHasInitialData] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [sortBy, setSortBy] = useState("student_name");

  const { limit } = useDynamicPageSize({ utilityHeight: 90, tableRowHeight: 45 });

  const { search: searchParams } = useLocation();
  const stageFilter = useMemo(() => new URLSearchParams(searchParams).get("stage"), [searchParams]);

  const stages = useMemo(
    () =>
      stageFilter
        ?.split(",")
        .map(parseToInquiryStage)
        .filter((stage): stage is InquiryStage => Boolean(stage)),
    [stageFilter]
  );
  const {
    queryKey,
    data: { inquiries, totalPages } = { inquiries: [], totalPages: 0 },
    isFetching
  } = useInquiryList({
    search,
    stages,
    sortBy,
    limit,
    pageIndex
  });
  useInquiryStatus(inquiries, queryKey);
  const { data: preselectedInquiry } = useInquirySummary(preselectedInquiryId);

  const { activeCenterId, onChangeActiveCenter } = useAccessControl();
  const { centers } = useCenters();
  const navigate = useNavigate();
  const { profile } = useMyProfile();
  const queryClient = useQueryClient();
  const { getToken } = useAccessToken();
  const confirmModal = useRef<ConfirmHandle>(null);
  const [checkPreselectedInquiry, setCheckPreselectedInquiry] = useState<boolean>(true);

  useEffect(() => {
    if (!isFetching && inquiries && !hasInitialData && !preselectedInquiry) {
      if (!selectedInquiry) {
        setSelectedInquiry(inquiries[0]);
        onInquirySelect?.(inquiries[0]);
      } else {
        const inquiryToSelect = inquiries.find(inquiry => inquiry.id === selectedInquiry.id);
        if (!inquiryToSelect) {
          setSelectedInquiry(inquiries[0]);
          onInquirySelect?.(inquiries[0]);
        } else {
          setSelectedInquiry(inquiryToSelect);
        }
      }
      setHasInitialData(true);
    }
  }, [isFetching, inquiries, hasInitialData]);

  useEffect(() => {
    if (preselectedInquiry && checkPreselectedInquiry) {
      setCheckPreselectedInquiry(false);
      const { centerId } = preselectedInquiry;

      if (activeCenterId === centerId) {
        setSelectedInquiry(preselectedInquiry);
        onInquirySelect?.(preselectedInquiry);
        return;
      }

      if (!centers?.some(center => center.id === centerId)) {
        window.toasts.add({
          content: "You do not have permission to access this inquiry's center",
          type: "danger"
        });
        navigate("/inquiry");
        setSelectedInquiry(inquiries[0]);
        onInquirySelect?.(inquiries[0]);
        return;
      }

      prefetchPermissions(centerId, profile?.id);
      confirmModal?.current?.confirm(
        () => {
          onChangeActiveCenter?.(centerId);
          setSelectedInquiry(preselectedInquiry);
          onInquirySelect?.(preselectedInquiry);
        },
        () => {
          navigate("/inquiry");
          setSelectedInquiry(inquiries[0]);
          onInquirySelect?.(inquiries[0]);
        }
      );
    }
  }, [preselectedInquiry]);

  useEffect(() => {
    setHasInitialData(false);
  }, [stages]);

  // Can maybe move this into the hook instead to some onStatusChange function
  useEffect(() => {
    if (selectedInquiry && inquiries?.length) {
      const reloadedSelectedInquiry = inquiries.find(i => i.id === selectedInquiry.id);
      if (reloadedSelectedInquiry) {
        setSelectedInquiry(reloadedSelectedInquiry);
        onInquirySelect?.(reloadedSelectedInquiry);
      }
    }
  }, [inquiries]);

  const prefetchPermissions = (centerId: string, staffMemberId?: string) => {
    if (centerId && staffMemberId) {
      queryClient.prefetchQuery({
        queryKey: ["staffMemberPermissionsData", centerId, staffMemberId],
        queryFn: async () => getStaffMemberPermissions(await getToken(), centerId, staffMemberId)
      });
    }
  };

  const startDateColumnName = (() => {
    if (stages?.length === 1) {
      switch (stages[0]) {
        case "INQ":
          return "Inquiry Start Date";
        case "AE":
          return "AE Start Date";
        case "ENR":
          return "Enrolled Start Date";
        case "DROP":
          return "Dropped Date";
        case "CONS_CONF":
          return "Consult/Conference Start Date";
      }
    }
    return "Status Start Date";
  })();

  const inquiryListHeaders: SortableTableHeader<Inquiry>[] = [
    {
      renderKey: "student",
      name: "Student",
      tableDataCellProps: { isPrimary: true },
      sortOptions: { enableSort: true, customSortKey: "student_name" },
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      render: item => <a href="#">{item.studentName}</a>
    },
    {
      key: "service",
      name: "Service",
      sortOptions: { enableSort: true }
    },
    ...(stageFilter
      ? []
      : [
          {
            key: "stage" as keyof Inquiry,
            name: "Status",
            sortOptions: { enableSort: true }
          }
        ]),
    {
      renderKey: "stageStartDate",
      name: startDateColumnName,
      render: item => formatDateString(item.stageStartDate),
      sortOptions: { enableSort: true, customSortKey: "inquiry_date" }
    }
  ];

  const handleSelectItem = (item: Inquiry) => {
    setSelectedInquiry(item);
    onInquirySelect?.(item);
  };

  const handleSortByChange = useCallback(
    (newSort: string) => {
      setPageIndex(0);
      setSortBy(oldSort => {
        if (oldSort === newSort) {
          return `-${newSort}`;
        }
        return newSort;
      });
    },
    [setSortBy]
  );

  useEffect(() => {
    setPageIndex(0);
  }, [searchParams, search, limit, sortBy]);

  useEffect(() => {
    setSortBy("student_name");
    onStatusChange?.();
  }, [searchParams]);

  const paginationOptions = {
    itemsPerPage: limit,
    onPageChange: (pageNumber: number) => setPageIndex(pageNumber - 1)
  };

  // TODO: might be wiser to create a hook factory for manual pagination
  const useInquiryPaginatedSorter: UsePaginatedSorterHook<Inquiry> = ({
    data,
    paginationOptions: { onPageChange } = {},
    isLoading
  }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig<Inquiry>>({
      key: null,
      secondaryKey: null,
      direction: "asc"
    });

    const { renderPaginator, setCurrentPage } = usePaginator({
      onPageChange,
      isLoading,
      numberOfPages: totalPages
    });

    const requestSort = ({ key, sortOptions }: SortableTableHeader<Inquiry>) => {
      let direction: SortDirection = "asc";
      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "des";
      }
      setSortConfig({ key: key ?? null, secondaryKey: null, direction });
      handleSortByChange(sortOptions?.customSortKey ?? key ?? "");
      setCurrentPage(1);
    };

    return {
      paginatedData: data,
      requestSort,
      renderPaginator
    };
  };

  return (
    <>
      <SortableTable
        tableCollapseScreenSize="sm"
        headers={inquiryListHeaders}
        data={inquiries}
        activeItem={selectedInquiry}
        paginationOptions={paginationOptions}
        usePaginatedSorter={useInquiryPaginatedSorter}
        isLoading={isFetching}
        onRowClick={handleSelectItem}
        tableRef={tableRef}
        uniqueIdKey="id"
        printCaption="Inquiries"
      />
      <Confirm
        ref={confirmModal}
        title="Confirm"
        message={`Do you want to change your currently active center to view this inquiry?`}
      />
    </>
  );
};
